interface EmailBodyViewProps {
  bodyHtml?: string | null
  bodyText?: string
  title?: string
  className?: string
}

/**
 * Renders an email body. Prefers HTML inside a sandboxed iframe (no scripts,
 * no same-origin) so the email's CSS can't escape and its JS can't run. Falls
 * back to plain text when no HTML is available.
 */
export default function EmailBodyView({ bodyHtml, bodyText, title, className }: EmailBodyViewProps) {
  if (bodyHtml) {
    return (
      <iframe
        title={title || 'Email body'}
        sandbox=""
        srcDoc={buildHtmlDoc(bodyHtml)}
        className={className ?? 'flex-1 w-full min-h-[320px] bg-white rounded-lg'}
      />
    )
  }

  return (
    <div className={className ?? 'flex-1 overflow-y-auto text-sm text-ivory/80 leading-relaxed whitespace-pre-line'}>
      {bodyText || ''}
    </div>
  )
}

const BASE_STYLES = `
  body { margin: 16px; font-family: -apple-system, system-ui, sans-serif; color: #111; word-wrap: break-word; line-height: 1.5; }
  img { max-width: 100%; height: auto; }
  a { color: #1a56db; }
  p { margin: 0 0 1em; }
`

function buildHtmlDoc(html: string): string {
  // If the HTML lacks any block-level structure, treat embedded \n as line
  // breaks so plain-text-in-HTML emails (e.g. assistant-generated welcomes)
  // don't render as one collapsed paragraph.
  const hasStructure = /<(p|br|div|table|tr|td|h[1-6]|ul|ol|li|blockquote)\b/i.test(html)
  const body = hasStructure ? html : textToHtml(html)
  return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>${BASE_STYLES}</style></head><body>${body}</body></html>`
}

/** Convert a plain-text-ish string to HTML: blank lines → paragraphs, single newlines → <br>. */
function textToHtml(input: string): string {
  const normalized = input.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim()
  if (!normalized) return ''
  return normalized
    .split(/\n{2,}/)
    .map(block => `<p>${block.replace(/\n/g, '<br>')}</p>`)
    .join('')
}
